import { urlConfig } from "@/config";
import { apiSlice } from "../../app/api/apiSlice";

/** Leaderboard rewards programme (task-list 5.0 T11). Board rows arrive masked from the server. */
export type LeaderboardBoard = "gift-card" | "crypto";
export type PeriodType = "daily" | "monthly";
export const BOARDS: LeaderboardBoard[] = ["gift-card", "crypto"];
export const BOARD_LABEL: Record<LeaderboardBoard, string> = { "gift-card": "Gift Card Leaderboard", crypto: "Crypto Leaderboard" };
export const BOARD_SHORT: Record<LeaderboardBoard, string> = { "gift-card": "Gift Card", crypto: "Crypto" };

export interface PrizeRow { rank: number; amount: number; source: "daily" | "fixed" | "shared" }
export interface BoardRow { rank: number; displayName: string; prize: number; qualified: boolean; movement: number | null; isYou: boolean }
export interface BoardResponse {
  board: LeaderboardBoard; label: string; periodType: PeriodType; periodKey: string; startsAt: string; endsAt: string; settlesAt: string;
  locked: boolean; programmeEnabled: boolean; poolTotal: number; minQualifyingVolume: number; participants: number; prizes: PrizeRow[]; rows: BoardRow[];
}
export interface PeriodSummary { periodKey: string; status: string; settledAt: string | null; poolTotal: number; rewards: number; winnerDisplayName: string | null }
export interface MyRanking {
  periodKey: string; rank: number | null; prevRank: number | null; movement: number | null; volume: number; qualified: boolean; prize: number;
  gapToAbove: number | null; gapToBelow: number | null; gapToTop10: number | null;
}
export interface RewardView {
  _id: string; board: LeaderboardBoard; periodType: PeriodType; periodKey: string; rank: number; amount: number;
  status: "credited" | "revoked" | "pending" | "failed"; createdAt?: string; seenAt?: string | null; revokedAt?: string; revokeReason?: string;
}
/** Legacy rows (masked name + rank only). */
export interface LeaderboardEntry { _id: string; user: { name: string }; rankBySells?: number; rankByDeposits?: number }

const base = `${urlConfig.leaderboardsUrl}/leaderboards`;
const qs = (params: Record<string, unknown>) =>
  Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== "")
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join("&");

export const leaderboardSlice = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    getBoard: build.query<BoardResponse, { board: LeaderboardBoard; periodType: PeriodType; periodKey?: string; limit?: number }>({
      query: ({ board, ...params }) => `${base}/${board}/board?${qs(params)}`,
      providesTags: ["LeaderboardBoard"],
    }),
    getPeriods: build.query<PeriodSummary[], { board: LeaderboardBoard; periodType: PeriodType; limit?: number }>({
      query: ({ board, ...params }) => `${base}/${board}/periods?${qs(params)}`,
      providesTags: ["LeaderboardBoard"],
    }),
    // `userId` is part of the cache key only (never sent) so one account's data is never shown to the next.
    getMyRanking: build.query<MyRanking, { board: LeaderboardBoard; periodType: PeriodType; userId?: string }>({
      query: ({ userId: _u, ...params }) => `${base}/my-ranking?${qs(params)}`,
      providesTags: ["LeaderboardBoard"],
    }),
    getRewards: build.query<{ data: RewardView[]; total: number }, { page?: number; limit?: number; userId?: string }>({
      query: ({ userId: _u, ...params }) => `${base}/rewards?${qs(params)}`,
      providesTags: ["LeaderboardRewards"],
    }),
    getUnseenRewards: build.query<RewardView[], { userId?: string }>({
      query: () => `${base}/rewards/unseen`,
      providesTags: ["LeaderboardRewards"],
    }),
    markRewardSeen: build.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `${base}/rewards/${id}/seen`, method: "PATCH" }),
      invalidatesTags: ["LeaderboardRewards"],
    }),
    getTopSellers: build.query<LeaderboardEntry[], void>({ query: () => `${base}/top-sellers/`, providesTags: ["TopSellers"] }),
    getTopDepositors: build.query<LeaderboardEntry[], void>({ query: () => `${base}/top-depositors/`, providesTags: ["TopDepositors"] }),
  }),
  overrideExisting: true,
});

export const {
  useGetBoardQuery, useGetPeriodsQuery, useGetMyRankingQuery, useGetRewardsQuery, useGetUnseenRewardsQuery, useMarkRewardSeenMutation,
  useGetTopSellersQuery, useGetTopDepositorsQuery,
} = leaderboardSlice;
